import { useEffect, useRef, useState } from 'react'
import { PlusIcon, PlayIcon, PauseIcon, TrashIcon, EyeIcon, BanknotesIcon, PencilIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'
import { TablePagination } from '@/components/ui/ResponsiveTable'
import StakingEditModal from '@/components/modals/StakingEditModal'
import Input from '@/components/ui/Input'

const PAGE_SIZE = 10
const USER_SUGGESTION_LIMIT = 500
const DROPDOWN_DISPLAY_LIMIT = 50

export default function StakingPage() {
  const [stakingConfigs, setStakingConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedStaking, setSelectedStaking] = useState(null)
  const [users, setUsers] = useState([])
  const [statistics, setStatistics] = useState(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [userSearchResults, setUserSearchResults] = useState([])
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    fetchStakingConfigs(currentPage)
  }, [filter, currentPage, searchTerm])

  useEffect(() => {
    fetchUsers()
    fetchStatistics()

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const fetchStakingConfigs = async (page = currentPage) => {
    try {
      setLoading(true)
      
      const params = {
        page,
        limit: PAGE_SIZE
      }
      if (filter !== 'all') {
        if (filter === 'active') params.isActive = true
        if (filter === 'inactive') params.isActive = false
        if (filter === 'krw') params.currency = 'krw'
        if (filter === 'token') params.currency = 'token'
      }
      if (searchTerm) {
        params.search = searchTerm
      }
      
      const response = await api.get('/staking', { params })
      
      if (response.data.success) {
        const { stakingConfigs: configs = [], pagination = {} } = response.data.data || {}
        const resolvedTotalPages = Math.max(1, pagination.totalPages || 1)

        setTotalPages(resolvedTotalPages)

        if (page > resolvedTotalPages) {
          if (currentPage !== resolvedTotalPages) {
            setCurrentPage(resolvedTotalPages)
          }
          return
        }

        setStakingConfigs(configs)

        const resolvedPage = pagination.page || page
        if (resolvedPage !== currentPage) {
          setCurrentPage(resolvedPage)
        }
      }
    } catch (error) {
      console.error('Failed to fetch staking configs:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await api.users.getUsers({ page: 1, limit: USER_SUGGESTION_LIMIT })
      if (response.success) {
        setUsers(response.data.users || [])
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
    }
  }

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/staking/statistics')
      if (response.data.success) {
        setStatistics(response.data.data.overview)
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error)
    }
  }

  const getStatusLabel = (isActive) => {
    return isActive ? '활성' : '비활성'
  }

  const getStatusColor = (isActive) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800'
  }

  const getCurrencyLabel = (currency) => {
    return currency === 'krw' ? '원화' : '토큰'
  }

  const formatAmount = (amount, currency) => {
    const num = Number(amount || 0)
    return currency === 'krw' 
      ? `₩${num.toLocaleString()}` 
      : num.toLocaleString()
  }

  const refreshStakingData = (page = currentPage) => {
    fetchStakingConfigs(page)
    fetchStatistics()
  }

  const handleEditStaking = (staking) => {
    setSelectedStaking(staking)
    setShowEditModal(true)
  }

  const handleEditSuccess = () => {
    refreshStakingData()
    alert('스테이킹 설정이 성공적으로 수정되었습니다!')
  }

  const handleFilterChange = (value) => {
    setFilter(value)
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    const trimmed = searchInput.trim()
    setCurrentPage(1)
    setSearchTerm(trimmed)
  }

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value)
  }

  const handleUserSearch = (term) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    const trimmed = term.trim()
    if (trimmed.length < 2) {
      setUserSearchResults([])
      setIsSearchingUsers(false)
      return
    }

    setUserSearchResults([])
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearchingUsers(true)
      try {
        const response = await api.users.getUsers({
          page: 1,
          limit: USER_SUGGESTION_LIMIT,
          search: trimmed
        })

        if (response.success) {
          const { users: fetched = [] } = response.data || {}
          setUserSearchResults(fetched)
        } else {
          setUserSearchResults([])
        }
      } catch (error) {
        console.error('Failed to search users:', error)
        setUserSearchResults([])
      } finally {
        setIsSearchingUsers(false)
      }
    }, 300)
  }

  const resetUserSearch = () => {
    setUserSearchResults([])
    setIsSearchingUsers(false)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">스테이킹 관리</h1>
        <button
          onClick={() => {
            resetUserSearch()
            setShowCreateModal(true)
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          스테이킹 설정
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">전체 설정</h3>
            <p className="text-2xl font-bold text-gray-900">{statistics.total_configs}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">활성 설정</h3>
            <p className="text-2xl font-bold text-green-600">{statistics.active_configs}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">일일 KRW</h3>
            <p className="text-2xl font-bold text-blue-600">
              ₩{Number(statistics.total_daily_krw || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">일일 토큰</h3>
            <p className="text-2xl font-bold text-purple-600">
              {Number(statistics.total_daily_tokens || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">비활성 설정</h3>
            <p className="text-2xl font-bold text-gray-600">{statistics.inactive_configs}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: '전체' },
            { key: 'active', label: '활성' },
            { key: 'inactive', label: '비활성' },
            { key: 'krw', label: 'KRW' },
            { key: 'token', label: '토큰' }
          ].map((item) => (
              <button
                key={item.key}
                onClick={() => handleFilterChange(item.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  filter === item.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSearchSubmit} className="mt-4 flex flex-col gap-2 md:flex-row md:items-center">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="사용자명, 닉네임 또는 이름으로 검색"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            검색
          </button>
        </form>
      </div>

      {/* Staking Configs Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                사용자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                통화
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                일일 보상
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                마지막 보상
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stakingConfigs.map((config) => (
              <tr key={config.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    <div className="font-medium">
                      {config.user?.name || config.user?.nickname || config.user?.username || '이름 정보 없음'}
                    </div>
                    <div className="text-gray-500">
                      @{config.user?.username || '정보없음'} • ID: {config.userId}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    config.currency === 'krw' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {getCurrencyLabel(config.currency)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  {formatAmount(config.dailyAmount, config.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(config.isActive)}`}>
                    {getStatusLabel(config.isActive)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {config.lastRewardDate 
                    ? new Date(config.lastRewardDate).toLocaleDateString('ko-KR')
                    : '없음'
                  }
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(config.createdAt).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <StakingActionButton
                      config={config}
                      onUpdate={() => refreshStakingData()}
                      onEdit={handleEditStaking}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stakingConfigs.length === 0 && (
        <div className="text-center py-12">
          <BanknotesIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">스테이킹 설정이 없습니다.</p>
        </div>
      )}

      {stakingConfigs.length > 0 && totalPages > 1 && (
        <div className="flex justify-end mt-4">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showPageNumbers
          />
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateStakingModal
          isOpen={showCreateModal}
          onClose={() => {
            resetUserSearch()
            setShowCreateModal(false)
          }}
          users={users}
          userSearchResults={userSearchResults}
          onSearchUsers={handleUserSearch}
          onResetSearch={resetUserSearch}
          isSearchingUsers={isSearchingUsers}
          onCreated={() => {
            refreshStakingData(1)
          }}
        />
      )}

      {/* Edit Modal */}
      <StakingEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedStaking(null)
        }}
        stakingConfig={selectedStaking}
        onUpdate={handleEditSuccess}
      />
    </div>
  )
}

// Staking Action Button Component
function StakingActionButton({ config, onUpdate, onEdit }) {
  const [loading, setLoading] = useState(false)

  const toggleStatus = async () => {
    try {
      setLoading(true)
      await api.put(`/staking/${config.id}`, {
        isActive: !config.isActive
      })
      onUpdate()
    } catch (error) {
      console.error('Failed to update staking config:', error)
      alert('스테이킹 설정 업데이트에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const deleteConfig = async () => {
    if (!confirm('이 스테이킹 설정을 삭제하시겠습니까?')) return

    try {
      setLoading(true)
      await api.delete(`/staking/${config.id}`)
      onUpdate()
    } catch (error) {
      console.error('Failed to delete staking config:', error)
      alert('스테이킹 설정 삭제에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        onClick={() => onEdit(config)}
        disabled={loading}
        className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
        title="수정"
      >
        <PencilIcon className="h-4 w-4" />
      </button>
      <button
        onClick={toggleStatus}
        disabled={loading}
        className={`${
          config.isActive 
            ? 'text-orange-600 hover:text-orange-900' 
            : 'text-green-600 hover:text-green-900'
        } disabled:opacity-50`}
        title={config.isActive ? '비활성화' : '활성화'}
      >
        {config.isActive ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
      </button>
      <button
        onClick={deleteConfig}
        disabled={loading}
        className="text-red-600 hover:text-red-900 disabled:opacity-50"
        title="삭제"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </>
  )
}

// Create Staking Modal Component
function CreateStakingModal({
  isOpen,
  onClose,
  users,
  userSearchResults,
  onSearchUsers,
  onResetSearch,
  isSearchingUsers,
  onCreated
}) {
  const [selectedUserId, setSelectedUserId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [currency, setCurrency] = useState('krw')
  const [dailyAmount, setDailyAmount] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Reset form when modal closes
  const handleClose = () => {
    setSelectedUserId('')
    setSearchTerm('')
    setShowUserDropdown(false)
    setCurrency('krw')
    setDailyAmount('')
    setDescription('')
    setError('')
    onResetSearch?.()
    onClose()
  }

  const lowerTerm = searchTerm.toLowerCase()
  const localFilteredUsers = users.filter(user => 
    user.nickname?.toLowerCase().includes(lowerTerm) ||
    user.username?.toLowerCase().includes(lowerTerm) ||
    user.name?.toLowerCase().includes(lowerTerm)
  )

  const trimmedSearch = searchTerm.trim()
  const isSearchActive = trimmedSearch.length >= 2

  const primaryUsers = isSearchActive ? userSearchResults : localFilteredUsers
  const supplementUsers = isSearchActive ? [] : users

  const candidateUsers = [...primaryUsers, ...supplementUsers].reduce((acc, user) => {
    if (!user || typeof user.id === 'undefined') {
      return acc
    }
    if (!acc.some(existing => existing.id === user.id)) {
      acc.push(user)
    }
    return acc
  }, [])

  const dropdownUsers = candidateUsers.slice(0, DROPDOWN_DISPLAY_LIMIT)

  const handleUserSelect = (user) => {
    setSelectedUserId(user.id.toString())
    const displayName = user.nickname || user.name || user.username
    setSearchTerm(`${displayName} (@${user.username})`)
    setShowUserDropdown(false)
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    setShowUserDropdown(true)
    if (!value) {
      setSelectedUserId('')
      onResetSearch?.()
    } else if (value.trim().length >= 2) {
      onSearchUsers?.(value)
    } else {
      onResetSearch?.()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!selectedUserId || !dailyAmount || parseFloat(dailyAmount) <= 0) {
      setError('모든 필수 필드를 올바르게 입력해주세요.')
      return
    }

    setSubmitting(true)
    setError('')

    const requestData = {
      userId: parseInt(selectedUserId),
      currency,
      dailyAmount: parseFloat(dailyAmount),
      description
    }
    
    try {
      await api.post('/staking', requestData)

      alert('스테이킹 설정이 성공적으로 생성되었습니다.')
      onCreated()
      handleClose()
    } catch (error) {
      console.error('Failed to create staking config:', error)
      console.error('Error details:', error.response?.data)
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          '스테이킹 설정 생성에 실패했습니다.'
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">새 스테이킹 설정</h3>
        <p className="text-sm text-gray-600 mb-4">각 사용자는 통화별로 하나의 활성 스테이킹 설정만 가질 수 있습니다.</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              사용자 선택
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowUserDropdown(true)}
              placeholder="사용자명, 닉네임 또는 이름으로 검색..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {showUserDropdown && dropdownUsers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                {dropdownUsers.map((user) => {
                  const displayNickname = user.nickname || user.name || user.username
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleUserSelect(user)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{displayNickname}</span>
                        <span className="text-gray-500">@{user.username}</span>
                      </div>
                      {user.name && (
                        <div className="text-sm text-gray-400">{user.name}</div>
                      )}
                    </button>
                  )
                })}
                {candidateUsers.length > DROPDOWN_DISPLAY_LIMIT && (
                  <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100 bg-gray-50">
                    검색어를 더 구체적으로 입력하면 추가 사용자를 볼 수 있습니다.
                  </div>
                )}
              </div>
            )}
            {showUserDropdown && isSearchActive && dropdownUsers.length === 0 && !isSearchingUsers && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-gray-500 text-center">
                검색 결과가 없습니다.
              </div>
            )}
            {showUserDropdown && isSearchActive && isSearchingUsers && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-gray-500 text-center">
                검색 중...
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              통화
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="currency"
                  value="krw"
                  checked={currency === 'krw'}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="mr-2"
                />
                KRW (원화)
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="currency"
                  value="token"
                  checked={currency === 'token'}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="mr-2"
                />
                토큰
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              일일 보상 금액
            </label>
            <input
              type="number"
              value={dailyAmount}
              onChange={(e) => setDailyAmount(e.target.value)}
              min="0"
              step="any"
              placeholder="일일 지급할 금액을 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              설명 (선택사항)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="스테이킹 설정에 대한 설명"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? '생성중...' : '생성하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
